import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, FavoritesChangedEvent } from '@theme/events';
import { Favorites } from '@theme/favorites';

/**
 * A custom element that displays a favorites icon with a live count bubble, mirroring cart-icon.js.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} favoritesBubble - The favorites bubble element.
 * @property {HTMLElement} favoritesBubbleText - The favorites bubble text element.
 * @property {HTMLElement} favoritesBubbleCount - The favorites bubble count element.
 *
 * @extends {Component<Refs>}
 */
class FavoritesIcon extends Component {
  requiredRefs = ['favoritesBubble', 'favoritesBubbleText', 'favoritesBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.favoritesChanged, this.onFavoritesChanged);
    this.renderBubble(Favorites.getAll().length, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.favoritesChanged, this.onFavoritesChanged);
  }

  /**
   * Handles the favorites changed event.
   * @param {Event} event - The favorites changed event.
   */
  onFavoritesChanged = (event) => {
    if (!(event instanceof FavoritesChangedEvent)) return;

    this.renderBubble(Favorites.getAll().length);
  };

  /**
   * Renders the favorites bubble.
   * @param {number} count - The number of favorited products.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderBubble = async (count, animate = true) => {
    this.refs.favoritesBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.favoritesBubble.classList.toggle('visually-hidden', count === 0);
    this.refs.favoritesBubbleCount.textContent = count > 0 && count < 100 ? String(count) : '';

    this.classList.toggle('header-actions__favorites-icon--has-items', count > 0);

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.favoritesBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.favoritesBubbleText);

    this.refs.favoritesBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('favorites-icon')) {
  customElements.define('favorites-icon', FavoritesIcon);
}
